import { Card, Drawer } from "antd";
import { useDrag, useDrop } from "react-dnd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import styles from "./DashBoardCard.module.css";
import { DashboardFilters } from "../DashboardFilters";
import CardTitle from "./CardTitle";
import { Chart } from "../../Chart";
import CardActions from "./CardActions";
import AdditionalInfo from "./AdditionalInfo";
import { exportToPNG } from "../../../utils/exportToPNG";
import useTopFiltersEffect from "../../../hooks/useTopFiltersEffect";
import useFieldValuesEffect from "../../../hooks/useFieldValuesEffect";
import { updateFiltersBasedOnSelection } from "../../../utils/dashboardCardUtils";
import { findInterval } from "../../../utils/findInterval";
import { getInitialDisplayName } from "../../../utils/getInitialDisplayName";
import { ConceptType, ConceptTypeVolume, DateBreakdown, maxDisabledValue } from "../../../constants";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { setDeselectionDate } from "../../../store/slices/dashboardSlice";
import { selectDashboardById } from "../../../store/selectors/selectors";
import { isConceptTypeValid } from "../../../utils/dashboardStatus";

export const DashBoardCard = (props) => {
    const {
        dataList,
        metaDataQu,
        isSuccess,
        widget,
        idx,
        data,
        setter,
        project,
        reset,
    } = props;

    const { id } = useParams();
    const { topOptionsConcepts, sentimentDisabled, totalDocCount } = useSelector((state) => state.project);

    const {
        topFilters = [],
        topConcepts = [],
        activeConceptList='',
        filterState: { deselection ='', deselectionConcepts= '', deselectionDate ='' } = {},
    } = useSelector((state) => selectDashboardById(state, id));

    const dispatch = useDispatch();
    const [filterDocCount, setFilterDocCount] = useState(totalDocCount);
    const metaDataDate = metaDataQu?.metaDataDate;

    const [additionalFilters, setAdditionalFilters] = useState([]);

    const [filters, setFilters] = useState(
        widget.dataProps ? widget.dataProps.filters : [],
    );
    const [collapsed, setCollapsed] = useState(false);
    const [selectedFieldName, setSelectedFieldName] = useState(
        widget.dataProps ? widget.dataProps.selectedFieldName : [],
    );
    const [selectedFieldDriverName, setSelectedFieldDriverName] = useState(
        widget.dataProps
            ? widget.dataProps.selectedFieldDriverName
            : metaDataQu?.metaDataDrivers[0]?.name,
    );
    const [selectedDateName, setSelectedDateName] = useState(
        widget.dataProps
            ? widget.dataProps.selectedDateName
            : metaDataQu?.metaDataDate[0]?.name || "",
    );
    const [fieldValues, setFieldValues] = useState([]);
    const [selectedFieldValue, setSelectedFieldValue] = useState(
        widget.dataProps ? widget.dataProps?.selectedFieldValue : [],
    );
    const [yAxis, setYAxis] = useState(widget.dataProps?.yAxis || []);
    const [scoreLines, setScoreLines] = useState(widget.dataProps?.scoreLines || []);
    const [conceptType, setConceptType] = useState(
        widget.dataProps ? widget.dataProps.conceptType : "",
    );
    const [selectedLocalConcepts, setSelectedLocalConcepts] = useState(
        widget.dataProps ? widget.dataProps.selectedLocalConcepts : [],
    );
    const [localConceptOptions, setLocalConceptOptions] = useState([
        ...topOptionsConcepts,
    ]);
    const [seletctedType, setSeletctedType] = useState(
        widget.dataProps ? widget.dataProps.seletctedType : "match_count",
    );
    const [selectedSortOption, setSelectedSortOption] = useState(
        widget.dataProps ? widget.dataProps.selectedSortOption : "type_desc",
    );
    const [selectedPageSize, setItemsPerPage] = useState(
        widget.dataProps ? widget.dataProps.selectedPageSize : "5",
    );
    const [conceptList, setConceptList] = useState(
        widget.dataProps ? widget.dataProps.conceptList : "",
    );
    const [textContent, setTextContent] = useState(
        widget.dataProps ? widget.dataProps.textContent : "<p>Click here to edit...</p>",
    );
    const metadata = props.metaDataQu.metaData;

    const selectedMetadata = metadata?.find((value) => value.name === selectedFieldDriverName);

    const { suitableInterval } = useMemo(() => findInterval(selectedMetadata), [selectedMetadata]);

    const [driverInterval, setDriverInterval] = useState(
        widget.dataProps ? widget.dataProps.driverInterval : suitableInterval || null,
    );
    const [breakdownName, setBreakdownName] = useState(
        widget.dataProps ? widget.dataProps?.breakdownName : metaDataQu.metaDataWithStringType[0]?.name,
    );

    const [displayName, setDisplayName] = useState(
        getInitialDisplayName(widget, selectedFieldDriverName),
    );
    const selectedDateField = useMemo(() => {
        return (
            metaDataQu?.metaDataDate.find(
                (field) => field.name === selectedDateName,
            ) || metaDataQu?.metaDataDate[0]
        );
    }, [metaDataQu?.metaDataDate, selectedDateName]);

    const defaultInterval = useMemo(() => {
        if (selectedDateField) {
            const { maximum, minimum } = selectedDateField;
            const startDate = moment(minimum);
            const endDate = moment(maximum);

            return DateBreakdown.INTERVALS.find((interval) => {
                return endDate.diff(startDate, interval) + 1 < maxDisabledValue;
            });
        }
    }, [selectedDateField]);

    const [interval, setIntervalDate] = useState(
        widget.dataProps ? widget.dataProps.interval : defaultInterval,
    );

    const [includeExamples, setIncludeExamples] = useState(false);
    const [visualizeType, setVisualizeType] = useState(true);

    const [conceptTypeDay, setConceptTypeDay] = useState(
        widget.dataProps
            ? widget.dataProps.conceptTypeDay
            : ConceptTypeVolume.Top,
    );
    const [smoothLine, setSmoothLine] = useState(widget.dataProps ? widget.dataProps.smoothLine : true);
    const ref = useRef(null);

    const dateTypeMetadata = useMemo(
        () =>
            metaDataDate.filter(
                (item) =>
                    item.type === "date" &&
                    selectedFieldName.includes(item.name),
            ),
        [metaDataDate, selectedFieldName],
    );

    useEffect(() => {
        if (topOptionsConcepts.length > 0) {
            setLocalConceptOptions([...topOptionsConcepts]);
        }
    }, [topOptionsConcepts]);

    useEffect(() => {
        if (deselectionDate) {
            setFilters((prevFilters) =>
                prevFilters.filter((el) => el.name !== deselectionDate),
            );
            setSelectedFieldName((prevFieldNames) =>
                prevFieldNames.filter((name) => name !== deselectionDate),
            );
            dispatch(setDeselectionDate({ id, deselectionDate }));
        }
    }, [deselectionDate]);

    useEffect(() => {
        if (!conceptList) {
            setLocalConceptOptions(topOptionsConcepts);
        }
    }, [conceptList]);

    useTopFiltersEffect({
        topFilters,
        metaDataDate: metaDataQu?.metaDataDate,
        setFilters,
        setSelectedFieldName,
        setSelectedFieldValue,
        reset,
    });

    useEffect(() => {
        if (!deselection || !deselection.name || !deselection.values) return;

        setFilters((prevFilters) => {
            return prevFilters.map((filter) => {
                if (
                    filter.name === deselection.name &&
                    deselection.type !== "number"
                ) {
                    return {
                        ...filter,
                        values: filter.values.filter(
                            (value) => !deselection.values.includes(value),
                        ),
                    };
                }
                return filter;
            });
        });

        setSelectedFieldValue((prevFieldValue) =>
            prevFieldValue.filter(
                (value) => !deselection.values.includes(value),
            ),
        );
    }, [deselection]);

    useEffect(() => {
        setSelectedLocalConcepts((prevConcepts) => {
            const preparedPrevConcepts = Array.isArray(prevConcepts)
                ? prevConcepts
                : [];
            const newConceptsSet = new Set([
                ...preparedPrevConcepts,
                ...topConcepts,
            ]);
            return Array.from(newConceptsSet);
        });
    }, [topConcepts]);

    useEffect(() => {
        setSelectedLocalConcepts((prevConcepts) => {
            return prevConcepts.filter(
                (concept) => !deselectionConcepts.includes(concept),
            );
        });
    }, [deselectionConcepts]);

    useEffect(() => {
        if (activeConceptList) {
            if (isConceptTypeValid(activeConceptList)) {
                setConceptTypeDay(activeConceptList);
                setConceptList(null);
            } else {
                setConceptList(activeConceptList);
                setConceptTypeDay(null);
            }
        }
    }, [activeConceptList, setConceptTypeDay, setConceptList]);

    const removeWidget = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const result = data.filter((el) => el.id !== widget.id);
        setter(result);
    };

    const handleChangeCardOrder = (startIdx, endIdx) => {
        const res = data.map((element, index) => {
            if (index === startIdx) {
                return data[endIdx];
            }
            if (index === endIdx) {
                return data[startIdx];
            }
            return element;
        });
        setter(res);
    };

    useEffect(() => {
        setFilters((prev) =>
            prev?.filter((el) => selectedFieldName.includes(el.name)),
        );
    }, [selectedFieldName]);

    const showFilters = () => {
        setCollapsed(true);
    };

    const hideFilters = () => {
        setCollapsed(false);
    };

    const [{ isDragging }, drag] = useDrag({
        type: "card",
        item: { idx },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });
    const [_, drop] = useDrop({
        accept: "card",
        hover(item, monitor) {
            if (!ref.current) return;

            const dragIndex = item.idx;
            const hoverIndex = idx;

            if (dragIndex === hoverIndex) return;

            const hoverBoundingRect = ref.current?.getBoundingClientRect();
            const clientOffset = monitor.getClientOffset();

            const hoverMiddleY =
                (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const hoverMiddleX =
                (hoverBoundingRect.right - hoverBoundingRect.left) / 2;

            const hoverClientY = clientOffset.y - hoverBoundingRect.top;
            const hoverClientX = clientOffset.x - hoverBoundingRect.left;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
            if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) return;
            if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX) return;

            handleChangeCardOrder(dragIndex, hoverIndex);
            item.idx = hoverIndex;
        },
    });

    drag(drop(ref));
    const opacity = isDragging ? 0 : 1;

    const dataProps = {
        id,
        selectedFieldValue,
        selectedFieldName,
        setSelectedFieldName,
        setFieldValues,
        setSelectedFieldValue,
        conceptType,
        setConceptType,
        setSelectedLocalConcepts,
        localConceptOptions,
        selectedLocalConcepts,
        seletctedType,
        setSeletctedType,
        widgetItem: widget.gridData,
        setSelectedSortOption,
        selectedSortOption,
        fieldValues,
        widget,
        filters,
        setFilters,
        selectedFieldDriverName,
        setSelectedFieldDriverName,
        totalDocCount,
        filterDocCount,
        metaDataQu,
        selectedPageSize,
        setItemsPerPage,
        conceptTypeDay,
        setConceptTypeDay,
        selectedDateName,
        setSelectedDateName,
        interval,
        setIntervalDate,
        dataList,
        conceptList,
        setConceptList,
        sentimentDisabled,
        setLocalConceptOptions,
        setFilterDocCount,
        includeExamples,
        setIncludeExamples,
        visualizeType,
        setVisualizeType,
        selectedDateField,
        dateTypeMetadata,
        project,
        setAdditionalFilters,
        displayName, setDisplayName,
        textContent,
        setTextContent,
        driverInterval, setDriverInterval,
        removeWidget,
        smoothLine, setSmoothLine,
        breakdownName, setBreakdownName,
        scoreLines, setScoreLines,
        yAxis, setYAxis
    };

    useEffect(() => {
        const widgetState = {
            selectedFieldName,
            selectedFieldValue,
            conceptType,
            selectedLocalConcepts,
            seletctedType,
            selectedSortOption,
            filters,
            selectedFieldDriverName,
            selectedPageSize,
            setItemsPerPage,
            conceptTypeDay,
            setSelectedFieldDriverName,
            selectedDateName,
            interval,
            conceptList,
            displayName,
            textContent,
            driverInterval,
            smoothLine,
            breakdownName,
            scoreLines,
            yAxis
        };
        props.onWidgetStateChange(widget.id, widgetState);
    }, [
        selectedFieldName,
        selectedFieldValue,
        conceptType,
        selectedLocalConcepts,
        seletctedType,
        selectedSortOption,
        selectedPageSize,
        setItemsPerPage,
        conceptTypeDay,
        setConceptTypeDay,
        selectedFieldDriverName,
        filters,
        selectedDateName,
        interval,
        conceptList,
        displayName,
        textContent,
        driverInterval, smoothLine,
        breakdownName,
        scoreLines,
        yAxis
    ]);

    useFieldValuesEffect({
        selectedFieldName,
        metaData: metaDataQu?.metaData,
        setFieldValues,
        selectedFieldValue,
        setSelectedFieldValue,
    });

    const updateFilters = () => {
        const updatedFilters = updateFiltersBasedOnSelection(
            selectedFieldName,
            selectedFieldValue,
            metaDataQu,
            filters,
            topFilters,
        );
        setFilters(updatedFilters);
    };

    useEffect(() => {
        updateFilters();
    }, [selectedFieldName, selectedFieldValue,topFilters]);


    const chartRef = useRef(null);

    const handleExportToPNG = () => {
        exportToPNG(ref.current, "dashboard-card.png");
    };
    return (
        <>
            <Card id={widget.id} onMouseDown={e => collapsed ? e.stopPropagation() : ""}
                  className={styles.card}
                  ref={ref}
                  data-testid="dashboardCard"
                  style={{ opacity }}
                  bodyStyle={{
                      height: "83%",
                  }}
                  title={
                      !widget.gridData.disable_header ? (
                          <>
                              <div style={{ display: "flex", justifyContent: "space-between" }}>
                                  <CardTitle
                                      setDisplayName={setDisplayName}
                                      displayName={displayName}
                                      selectedFieldDriverName={selectedFieldDriverName}
                                      widget={widget}
                                      docCount={totalDocCount}
                                      totalDocCount={totalDocCount}
                                      filterDocCount={filterDocCount}
                                      selectedFieldName={selectedFieldName}
                                      filters={filters}
                                      dateTypeMetadata={dateTypeMetadata}
                                  />
                                  <CardActions
                                      removeWidget={removeWidget}
                                      widget={widget}
                                      exportToPNG={handleExportToPNG}
                                      isSuccess={isSuccess}
                                      showFilters={showFilters}
                                  />
                              </div>
                              <AdditionalInfo
                                  widget={widget}
                                  selectedFieldName={selectedFieldName}
                                  docCount={totalDocCount}
                                  totalDocCount={totalDocCount}
                                  filterDocCount={filterDocCount}
                                  filters={filters}
                                  dateTypeMetadata={dateTypeMetadata}
                              />
                          </>
                      ) : null
                  }
            >
                <Drawer
                    onMouseDown={e => e.stopPropagation()}
                    onClose={hideFilters}
                    getContainer={false}
                    closable={false}
                    open={collapsed}
                    placement="left"
                    title="Options"
                    width={210}
                    data-testid="drawer"
                >
                    <DashboardFilters props={dataProps} additionalFilters={additionalFilters} />
                </Drawer>
                <Chart onMouseDown={e => e.stopPropagation()} ref={chartRef} widgetName={widget.text}
                       dataProps={dataProps} />
            </Card>
        </>
    );
};
